import {WORKSPACE_CAPABILITIES} from '@/utils/access-capabilities';
import {TEMPLATE_FAMILIES} from '@/utils/template-profiles';

function hasModule(access, moduleKey) {
    return !!(access && access.modules && access.modules[moduleKey]);
}

function hasCapability(access, capabilityKey) {
    return !!(access && access.capabilities && access.capabilities[capabilityKey]);
}

function routeMap(internalRoute, customerRoute = null, guestRoute = null) {
    return {
        [TEMPLATE_FAMILIES.INTERNAL]: internalRoute,
        [TEMPLATE_FAMILIES.CUSTOMER]: customerRoute,
        [TEMPLATE_FAMILIES.GUEST]: guestRoute,
    };
}

const NAVIGATION_ITEMS = [
    {
        id: 'workspace_dashboard',
        label: 'Dashboard',
        description: 'Navigiert zur Dashboard-Startseite.',
        icon: 'font-awesome.regular.grid-2-plus',
        exact: false,
        routes: routeMap('/workspace/dashboard', '/dashboard', '/workspace/dashboard'),
        visible({access}) {
            return access.template_family === TEMPLATE_FAMILIES.INTERNAL
                || access.template_family === TEMPLATE_FAMILIES.CUSTOMER
                || access.template_family === TEMPLATE_FAMILIES.GUEST;
        },
    },
    {
        id: 'relationships',
        label: 'Beziehungen',
        description: 'Navigiert zur Beziehungs-Uebersicht.',
        icon: 'font-awesome.regular.user-group',
        iconClass: 'main-menu-icon-size main-menu-icon-size-users text-gray-400 transition ease-in-out duration-200',
        exact: false,
        routes: routeMap('/workspace/relationships', '/relationships'),
        visible({access}) {
            return access.template_family !== TEMPLATE_FAMILIES.GUEST
                && hasModule(access, 'relationships');
        },
    },
    {
        id: 'tickets',
        label: 'Tickets',
        description: 'Navigiert zur Ticket-Uebersicht im Hauptmenue.',
        icon: 'font-awesome.regular.message-text',
        exact: false,
        routes: routeMap('/workspace/tickets', '/tickets/list', '/workspace/tickets'),
        visible({access}) {
            return access.template_family === TEMPLATE_FAMILIES.CUSTOMER
                ? true
                : hasModule(access, 'tickets');
        },
    },
    {
        id: 'projects',
        label: 'Projekte',
        description: 'Navigiert zur Projekt-Uebersicht.',
        icon: 'font-awesome.regular.layer-group',
        exact: true,
        routes: routeMap('/workspace/projects', '/projects/list', '/workspace/projects'),
        visible({access}) {
            return hasModule(access, 'projects')
                && (access.template_family !== TEMPLATE_FAMILIES.INTERNAL || hasCapability(access, WORKSPACE_CAPABILITIES.PROJECTS));
        },
    },
    {
        id: 'boards',
        label: 'Boards',
        description: 'Navigiert zu den Boards.',
        icon: 'font-awesome.regular.chart-kanban',
        exact: true,
        routes: routeMap('/workspace/boards', '/boards', '/workspace/boards'),
        visible({access}) {
            return hasModule(access, 'boards');
        },
    },
    {
        id: 'positions',
        label: 'Positionen',
        description: 'Navigiert zur Positions-Uebersicht.',
        icon: 'font-awesome.regular.diagram-next',
        exact: true,
        routes: routeMap('/workspace/positions', '/positions/list'),
        visible({access}) {
            return hasModule(access, 'positions')
                && access.template_family !== TEMPLATE_FAMILIES.GUEST;
        },
    },
    {
        id: 'timeline',
        label: 'Timeline',
        description: 'Navigiert zur Timeline im Hauptmenue.',
        icon: 'font-awesome.regular.timeline-arrow',
        exact: false,
        routes: routeMap('/workspace/timeline', '/timeline'),
        visible({access}) {
            return hasModule(access, 'timeline')
                && access.template_family !== TEMPLATE_FAMILIES.GUEST;
        },
    },
    {
        id: 'file_manager',
        label: 'Dateimanager',
        description: 'Navigiert zum Dateimanager.',
        icon: 'font-awesome.regular.folder-open',
        exact: false,
        routes: routeMap('/workspace/file-manager', '/file-manager/list', '/workspace/file-manager'),
        visible({access}) {
            return hasModule(access, 'file_manager');
        },
    },
    {
        id: 'documentation',
        label: 'Dokumentation',
        description: 'Navigiert zur Dokumentations-Uebersicht.',
        icon: 'font-awesome.regular.book-open-cover',
        exact: false,
        routes: routeMap('/workspace/documentation', '/documentations/list', '/documentations/list'),
        visible({access}) {
            if (access.template_family === TEMPLATE_FAMILIES.CUSTOMER) {
                return hasModule(access, 'customer_documentation');
            }

            if (access.template_family === TEMPLATE_FAMILIES.GUEST) {
                return hasModule(access, 'customer_documentation') || hasModule(access, 'documentation');
            }

            return hasModule(access, 'documentation');
        },
    },
    {
        id: 'vault',
        label: 'Sicherheitsnotizen',
        description: 'Navigiert zu den Sicherheitsnotizen.',
        icon: 'font-awesome.regular.shield-keyhole',
        exact: false,
        routes: routeMap('/workspace/vault', '/vault/list', '/workspace/vault'),
        visible({access}) {
            if (access.template_family === TEMPLATE_FAMILIES.CUSTOMER) {
                return access.is_customer && hasModule(access, 'vault');
            }

            return hasModule(access, 'vault');
        },
    },
    {
        id: 'insights',
        label: 'Insights',
        description: 'Navigiert zur Insights-Uebersicht.',
        icon: 'font-awesome.regular.monitor-waveform',
        showInMenu: false,
        exact: false,
        routes: routeMap('/workspace/insights', '/insights/server-monitoring', '/workspace/insights'),
        visible({access}) {
            if (access.template_family === TEMPLATE_FAMILIES.CUSTOMER) {
                return access.is_customer && hasModule(access, 'insights');
            }

            return hasModule(access, 'insights');
        },
    },
    {
        id: 'finance',
        label: 'Finanzen',
        description: 'Navigiert zur Finanz-Uebersicht.',
        icon: 'font-awesome.regular.calculator',
        exact: false,
        routes: routeMap('/workspace/finance', '/finance/list'),
        visible({access}) {
            if (access.template_family === TEMPLATE_FAMILIES.CUSTOMER) {
                return access.is_customer && hasModule(access, 'finance');
            }

            return hasModule(access, 'finance')
                && access.template_family !== TEMPLATE_FAMILIES.GUEST;
        },
    },
    {
        id: 'service_accounts',
        label: 'Leistungskonten',
        description: 'Navigiert zur Verwaltung der Leistungskonten.',
        icon: 'font-awesome.regular.box-arrow-down-arrow-up',
        exact: false,
        routes: routeMap('/workspace/service-accounts'),
        activeRoutes: ['/workspace/billing-profiles', '/workspace/bank-accounts'],
        visible({access}) {
            return access.template_family === TEMPLATE_FAMILIES.INTERNAL
                && hasModule(access, 'companies');
        },
    },
    {
        id: 'admin_cms',
        label: 'CMS',
        description: 'Navigiert zum CMS-Bereich.',
        icon: 'font-awesome.regular.window-restore',
        showInMenu: false,
        exact: true,
        routes: routeMap('/workspace/cms'),
        visible({access}) {
            return hasModule(access, 'cms');
        },
    },
    {
        id: 'system_extensions',
        label: 'Systemerweiterungen',
        description: 'Navigiert zur Uebersicht der aktivierten Systemerweiterungen.',
        icon: 'font-awesome.regular.cube',
        exact: false,
        routes: routeMap('/workspace/system-extensions', '/system-extensions'),
        activeRoutes: ['/workspace/cms', '/workspace/insights', '/insights'],
        visible({access}) {
            return access.template_family !== TEMPLATE_FAMILIES.GUEST
                && (hasModule(access, 'cms') || hasModule(access, 'insights'));
        },
    },
    {
        id: 'admin_users',
        label: 'Benutzerkonten',
        description: 'Navigiert zur Verwaltung der Benutzerkonten.',
        icon: 'font-awesome.regular.address-card',
        exact: false,
        routes: routeMap('/workspace/admin/users'),
        visible({access}) {
            return access.template_family === TEMPLATE_FAMILIES.INTERNAL
                && hasModule(access, 'admin_users');
        },
    },
    {
        id: 'admin_settings',
        label: 'Systemeinstellungen',
        description: 'Navigiert zur Uebersicht der Systemeinstellungen.',
        icon: 'font-awesome.regular.cog',
        exact: true,
        routes: routeMap('/workspace/admin/settings'),
        visible({access}) {
            return hasModule(access, 'settings');
        },
    },
];

export function getNavigationItems() {
    return NAVIGATION_ITEMS.map((item) => ({...item}));
}

export function resolveNavigationRouteForAccess(item, access) {
    if (!item || !access) {
        return null;
    }

    const templateFamily = access.template_family || TEMPLATE_FAMILIES.PUBLIC;
    return item.routes && item.routes[templateFamily]
        ? item.routes[templateFamily]
        : null;
}

export function resolveVisibleNavigationItems(access) {
    return NAVIGATION_ITEMS
        .filter((item) => item.showInMenu !== false)
        .filter((item) => typeof item.visible === 'function' ? item.visible({access}) : true)
        .map((item) => ({
            id: item.id,
            label: item.label,
            description: item.description,
            icon: item.icon,
            iconClass: item.iconClass || null,
            activeRoutes: Array.isArray(item.activeRoutes) ? [...item.activeRoutes] : [],
            exact: !!item.exact,
            route: resolveNavigationRouteForAccess(item, access),
        }))
        .filter((item) => typeof item.route === 'string' && item.route !== '');
}
